/* eslint-disable graphql-relay-compat/no-import-graphql-operations */
/* eslint-disable no-restricted-imports */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';

import type {
	SpaceHoverPageCardQuery as SpaceHoverPageCardQueryData,
	SpaceHoverPageCardQueryVariables as SpaceHoverPageCardQueryVars,
} from '@confluence/page-card/src/__types__/SpaceHoverPageCardQuery';
import { SpaceHoverPageCardQuery } from '@confluence/page-card/src/SpaceHoverPageCardQuery.graphql';
import { ReactionsContentQuery } from '@confluence/reactions/src/graphql/ReactionsContentQuery.graphql';
import {
	ReactionsContentQuery as ReactionsContentQueryData,
	ReactionsContentQueryVariables as ReactionsContentQueryVars,
} from '@confluence/reactions/src/graphql/__types__/ReactionsContentQuery';
import { getApolloClient } from '@confluence/graphql';
import { ReactedUsersQuery } from '@confluence/reactions/src/graphql/ReactedUsersQuery.graphql';
import {
	ContainerType,
	GraphQLReactionContentType,
	ReactedUsersQuery as ReactedUsersQueryData,
	ReactedUsersQueryVariables,
} from '@confluence/reactions/src/graphql/__types__/ReactedUsersQuery';
import { InlineReactionsData } from '@post-office/growth-common/inline-reactions-data';

import { getPageDataFromUrl } from './getPageDataFromUrl';

type ReactedUserQueryResult = {
	data?: string[];
	loading: boolean;
	error?: Error;
};

const INITIAL_REACTED_USER_QUERY_RESULT: ReactedUserQueryResult = {
	data: undefined,
	loading: false,
	error: undefined,
};

// Helper function to fetch the specific names of users who reacted to a specific emoji
const getReactedUsersForEmoji = async (contentId: string, contentType: string, emojiId: string) => {
	const { data } = await getApolloClient().query<ReactedUsersQueryData, ReactedUsersQueryVariables>(
		{
			query: ReactedUsersQuery,
			variables: {
				contentId,
				contentType: contentType.toUpperCase() as GraphQLReactionContentType,
				containerId: contentId,
				containerType: contentType.toUpperCase() as ContainerType,
				emojiId,
			},
		},
	);

	return data;
};

// Hook to fetch the page author, author's avatar URL, total reactions count, and the first
// {numberOfReactedUsers} users who reacted to the page.
export const useInlineReactionsData = (
	numberOfReactedUsers: number,
	skip: boolean = false,
): InlineReactionsData => {
	const { contentId, contentType } = getPageDataFromUrl(window.location.href);
	const [reactedUsers, setReactedUsers] = useState<ReactedUserQueryResult>(
		INITIAL_REACTED_USER_QUERY_RESULT,
	);

	// Fetch page author and author's avatar URL
	const {
		data: spaceHoverPageCardResult,
		loading: spaceHoverPageCardLoading,
		error: spaceHoverPageCardError,
	} = useQuery<SpaceHoverPageCardQueryData, SpaceHoverPageCardQueryVars>(SpaceHoverPageCardQuery, {
		variables: {
			id: contentId!, // guaranteed to be defined because we skip the query if it's not
		},
		skip: !contentId || skip,
	});

	// Fetch total reactions count and emoji ID summary
	const {
		data: reactionsContentData,
		loading: reactionsLoading,
		error: reactionsError,
	} = useQuery<ReactionsContentQueryData, ReactionsContentQueryVars>(ReactionsContentQuery, {
		variables: {
			contentId: contentId!, // guaranteed to be defined because we skip the query if it's not
		},
		skip: !contentId || skip,
	});

	// Use the emoji ID summary from reactionsContentData to fetch the specific names of users who reacted
	useEffect(() => {
		const emojiIds: string[] | undefined =
			reactionsContentData?.content?.nodes?.[0]?.contentReactionsSummary?.reactionsSummaryForEmoji?.flatMap(
				(reaction) => reaction?.emojiId ?? [],
			);

		// Need to put this in a function and then call it because we can't use async/await in useEffect
		const getReactedUsers = async () => {
			if (!contentId || !contentType || !emojiIds || reactedUsers.data || reactedUsers.loading) {
				return;
			}

			setReactedUsers({ loading: true });

			const uniqueUsers: Record<string, string> = {};

			// Process emojis in parallel until we have enough users
			await Promise.allSettled(
				emojiIds.filter(Boolean).map(async (emojiId) => {
					// Resolve the promise if we already have enough users
					if (Object.keys(uniqueUsers).length >= numberOfReactedUsers) {
						return;
					}

					const data = await getReactedUsersForEmoji(contentId, contentType, emojiId);

					// Add users until we have enough
					for (const user of data.reactedUsers.users) {
						if (Object.keys(uniqueUsers).length >= numberOfReactedUsers) {
							return;
						}
						if (user?.displayName && user.accountId) {
							uniqueUsers[user.accountId] = user.displayName;
						}
					}
				}),
			);

			return {
				data: Object.values(uniqueUsers),
				loading: false,
			};
		};

		getReactedUsers()
			.then((result) => {
				if (result) {
					setReactedUsers(result);
				}
			})
			.catch((error) => {
				setReactedUsers({
					loading: false,
					error: error as Error,
				});
			});
	}, [contentId, contentType, reactionsContentData, reactedUsers, numberOfReactedUsers]);

	// Transform this so that it matches Post Office's expected shape (for use with the ReactionWrapper component)
	const transformedReactionsSummary = useMemo(
		() => ({
			reactionsSummary: {
				ari: reactionsContentData?.content?.nodes?.[0]?.contentReactionsSummary?.ari,
				containerAri:
					reactionsContentData?.content?.nodes?.[0]?.contentReactionsSummary?.containerAri,
				reactionsSummaryForEmoji:
					reactionsContentData?.content?.nodes?.[0]?.contentReactionsSummary?.reactionsSummaryForEmoji.map(
						(emoji) => ({
							count: emoji?.count,
							emojiId: emoji?.emojiId,
							reacted: emoji?.reacted,
						}),
					),
			},
		}),
		[reactionsContentData],
	);

	return {
		data: {
			authorName:
				spaceHoverPageCardResult?.singleContent?.history?.ownedBy?.displayName ?? undefined,
			authorAvatar: spaceHoverPageCardResult?.singleContent?.history?.ownedBy?.profilePicture?.path,
			reactionsCount:
				reactionsContentData?.content?.nodes?.[0]?.contentReactionsSummary?.reactionsCount,
			reactionsSummary: transformedReactionsSummary,
			reactedUsers: reactedUsers.data,
		},
		loading: spaceHoverPageCardLoading || reactionsLoading || reactedUsers.loading,
		error: spaceHoverPageCardError || reactionsError || reactedUsers.error,
	};
};
